import { consola } from 'consola'
import { watch } from 'vue'
import { useCookieConsent } from '@shared/lib'
import {
  captureUtmFromUrl,
  initAnalyticsQueue,
  isAnalyticsEnabled,
  isCrossEnvironmentTarget,
  registerAnalyticsContextProvider,
  setAnalyticsDeviceId,
} from '@shared/lib/analytics'
import { getClientFingerprint } from '@shared/lib/auth/clientFingerprint'
import { useUserStore } from '@entities/user'
import { useInstitutionStore } from '@entities/institution'

const logger = consola.withTag('analytics')

/**
 * Поднимает слой аналитики: транспорт до своего event-store и мост к доменным сторам.
 *
 * Плагин живёт в слое приложения именно потому, что знает про entities: сам
 * `shared/lib/analytics` доменные слои не импортирует (правило FSD), а userId и филиал
 * ему нужны — без них воронку не связать ни с заказом, ни с деньгами.
 */
export default defineNuxtPlugin(() => {
  const config = useRuntimeConfig()

  // Метки рекламы снимаем ПЕРВЫМ делом и до любой навигации: после перехода внутри
  // сайта `utm_*` из адреса исчезают, и связь «реклама → заказ» теряется навсегда.
  // Ловим независимо от флага событий — атрибуция нужна и целям Метрики.
  if (captureUtmFromUrl()) {
    logger.debug('Зафиксированы метки рекламной кампании')
  }

  // Мост к сторам. Ленивый: сторы поднимаются позже плагинов, поэтому резолвим
  // на каждом событии, а не один раз здесь.
  registerAnalyticsContextProvider(() => {
    try {
      const userStore = useUserStore()
      const institutionStore = useInstitutionStore()

      // Именно `user.id`: computed `getUserId` из стора наружу не экспортируется.
      const userId = Number(userStore.user?.id)

      // Идентификаторы в сторах живут строками, а контракт события — числовой.
      // Без приведения бэк отвергал пачку целиком (400 на всю пачку, не на поле).
      const toId = (value: unknown): number | undefined => {
        const parsed = Number(value)
        return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined
      }

      return {
        userId: userId > 0 ? userId : undefined,
        institutionId: toId(institutionStore.institution?.id),
        branchId: toId(institutionStore.selectedBranchId),
      }
    } catch {
      // До инициализации Pinia событие уедет без привязки — это лучше, чем его потерять.
      return {}
    }
  })

  // Приёмник включается флагом: фронт живёт в бандле приложения дольше бэка,
  // поэтому выкат идёт бэк → прод → фронт, и до появления эндпоинта события не шлём.
  if (!isAnalyticsEnabled(config.public.analyticsEnabled)) {
    logger.debug('Свой event-store выключен (NUXT_PUBLIC_ANALYTICS_ENABLED)')
    return
  }

  // Локальный фронт, смотрящий на прод или стейдж, не имеет права писать в их отчёты:
  // события неотличимы от гостевых, а переключение адреса API — одна строка в .env.
  if (isCrossEnvironmentTarget(window.location.origin, String(config.public.baseUrl ?? ''))) {
    logger.warn(
      `Аналитика отключена: страница локальная, а API внешний (${config.public.baseUrl}). ` +
        'События не пишем, чтобы не смешать их с боевыми.',
    )
    return
  }

  const endpoint = `${config.public.baseUrl}${config.public.apiV2Prefix ?? ''}/analytics/events`

  // Свой event-store — такая же аналитика, как Метрика: события сшиваются с userId
  // и отпечатком устройства, то есть это обработка персональных данных, а не счётчик
  // посещений. Поэтому очередь поднимаем только после согласия на аналитику.
  const { analyticsAllowed } = useCookieConsent()

  let started = false

  const start = (): void => {
    if (started) return
    started = true

    // Отпечаток тот же, что уходит в авторизацию: он склеивает сессии одного устройства
    // до логина и после, иначе воронка «гость → зарегистрировался» рвётся. В саму
    // авторизацию он идёт независимо от этого согласия — там основание другое
    // (защита от подбора и мультиаккаунтов), и отказ от аналитики его не отменяет.
    void getClientFingerprint()
      .then(fingerprint => setAnalyticsDeviceId(fingerprint))
      .catch(() => setAnalyticsDeviceId(undefined))

    initAnalyticsQueue(endpoint)
    logger.debug(`Аналитика инициализирована: ${endpoint}`)
  }

  // Не `immediate`: при уже данном согласии колбэк отработал бы синхронно внутри
  // watch() — до присвоения переменной со «стопом», и вызов упал бы на TDZ.
  const handleConsent = (allowed: boolean): void => {
    if (!allowed) return
    start()
    stopWatching()
  }

  const stopWatching = watch(analyticsAllowed, handleConsent)

  // Текущее состояние проверяем после подписки — тогда stopWatching уже существует.
  handleConsent(analyticsAllowed.value)

  if (!analyticsAllowed.value) {
    logger.debug('Свой event-store ждёт согласия на аналитические cookie')
  }
})
